import { Badge } from "react-bootstrap";
import type {
  ConstrainedAllocationData,
  StopReason,
} from "../lib/explainTypes";
import { formatCurrency } from "../../../lib/numbers";

interface Props {
  constrained: ConstrainedAllocationData;
  isTaxable: boolean;
}

const STOP_REASON_LABELS: Record<StopReason, string> = {
  fully_traded: "Fully traded",
  tax_budget_cap: "Tax budget cap",
  st_gains_cap: "ST gains cap",
  lt_gains_cap: "LT gains cap",
  no_shares_left: "No shares left",
  buy_side_dropped: "Buy not funded",
  round_lot_floor: "Round lot floor",
  restriction_locked: "Restriction locked",
  substituted: "Substituted",
  excluded: "Excluded",
  residual_redeploy: "Residual redeploy",
};

const labelFor = (key: string): string =>
  STOP_REASON_LABELS[key as StopReason] ?? key;

function CountChips({
  counts,
  variant,
}: {
  counts: Record<string, number>;
  variant: "danger" | "secondary";
}) {
  const entries = Object.entries(counts).filter(([, n]) => n > 0);
  if (entries.length === 0) return <span className="text-muted">none</span>;
  return (
    <>
      {entries.map(([key, n]) => (
        <Badge key={key} bg={variant} className="me-1">
          {labelFor(key)} × {n}
        </Badge>
      ))}
    </>
  );
}

// Overview chips for the Constrain section: aggregate counts and
// realized gain rollups that summarise what happened in the
// constrain pass for this account. Per decision §3 (Overview chips
// in dc-constrain-section-ui-redesign-replace-019e1dc7).
export default function ConstrainOverview({ constrained, isTaxable }: Props) {
  const issueCounts = constrained.issue_counts ?? {};
  const byDesignCounts = constrained.by_design_counts ?? {};
  const lotCount = constrained.lot_adjustments.length;
  const symbolCount = Object.keys(constrained.final_target).length;
  const realizedSt = constrained.realized_st;
  const realizedLt = constrained.realized_lt;
  const showRealized =
    isTaxable &&
    (Number(realizedSt ?? 0) !== 0 || Number(realizedLt ?? 0) !== 0);

  return (
    <dl className="row mb-3 small">
      <dt className="col-sm-3 text-muted fw-normal">Lots considered</dt>
      <dd className="col-sm-9">
        {lotCount === 0 ? (
          <span className="text-muted">none</span>
        ) : (
          <>
            {lotCount} lot{lotCount === 1 ? "" : "s"} across {symbolCount}{" "}
            symbol{symbolCount === 1 ? "" : "s"}
          </>
        )}
      </dd>

      {showRealized && (
        <>
          <dt className="col-sm-3 text-muted fw-normal">Realized</dt>
          <dd className="col-sm-9">
            <span className="me-3">
              ST: {formatCurrency(Number(realizedSt ?? 0))}
            </span>
            <span>LT: {formatCurrency(Number(realizedLt ?? 0))}</span>
          </dd>
        </>
      )}

      <dt className="col-sm-3 text-muted fw-normal">Issues</dt>
      <dd className="col-sm-9">
        <CountChips counts={issueCounts} variant="danger" />
      </dd>

      <dt className="col-sm-3 text-muted fw-normal">By design</dt>
      <dd className="col-sm-9">
        <CountChips counts={byDesignCounts} variant="secondary" />
      </dd>
    </dl>
  );
}

export { STOP_REASON_LABELS, labelFor };
